import * as A from 'fp-ts/Array'

import { IVolumeControls, StreamTile } from './models'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { DatePipe, DOCUMENT } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { PlatformService } from '../singleton-services/platform.service'
import { VideoPlayerControlsPlayToggleClickEvent } from './components/video-player-controls/video-player-controls.interfaces'
import { ITab } from './components/tabs/tab.component'
import { AuthService } from '../singleton-services/auth/auth.service'
import { LoggingService } from 'src/app/singleton-services/logging/logging.service'
import { maybe } from 'typescript-monads'
import { SegmentService } from '../shared/analytics/services/segment.service'
import { FloGridListViewComponent } from '@flosportsinc/ng-grid-list'
import { UntypedFormControl } from '@angular/forms'
import { DeviceService } from '../singleton-services/device.service'
import { VerticalService } from '../singleton-services/vertical.service'
import { LiveEventAdMetadata, LiveEventService } from './services/event.service'
import { NodeService } from '../shared/services/node.service'
import { filterSortStreamObs, RealtimeEventStatus } from './live.reducers'
import { VideoPlayerComponent } from './components/video-player/video-player.component'
import { VideoPlayerScreenErrorCode } from '../shared/video-player/models/video-player-screen-error-code.model'
import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  TrackByFunction,
  ViewChild,
  ViewChildren
} from '@angular/core'
import { combineLatest, iif, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  flatMap,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  withLatestFrom
} from 'rxjs/operators'
import {
  IExtendedRealtimeCameraInfo,
  IRealtimeCameraInfoDictionary,
  IRealtimeEventResponseWithEventId
} from './models/realtime-event-response.model'
import { IVideoSkip } from './components/video-player-controls/video-player-controls.component'
import { SiteIds } from '../shared/models/site.model'
import { INode, NodeExternalAssociation } from '../shared/models/node.model'
import Hls from 'hls.js'
import { isIphone } from '@flosportsinc/ng-fullscreen'
import { EventsService } from '../events/services/events.service'
import { IVideo } from '../shared/models/video.model'
import { IVideoQuality } from './components/video-player/video-player.interfaces'
import { addSlugToUrl, buildCanonicalUrl, CANONICAL_ID } from '../singleton-services/seo/seo.utility'
import { UpdateHeadService } from '../singleton-services/seo/update-head.service'
import { monoidAll } from 'fp-ts/Monoid'
import { AdComponentInputs } from '../shared/ads/ad/ad-component.interface'
import { AdConfig, AdTargeting } from '../shared/ads/advertising-service/ad-config'
import { AD_ADHESION_SIZE, AD_INTERSTITIAL_SIZE, AD_RECTANGLE_LARGE_SIZE } from '../shared/ads/ads.utility'
import { SportDataSummary } from '../shared/sport-data/types'
import { InjectionService } from '../singleton-services/injection.service'
import { UserAgentService } from '../singleton-services/user-agent.service'
import { buildTypeformStreamFeedbackUrlFromProps } from './services/typeform.utility'
import { SPORT_DATA_SERVICE } from '../shared/sport-data/sport-data.config'
import { SportDataService } from '../shared/sport-data/sport-data.interface'
import {
  canShowLiveVideoControls,
  extractEventHubLinkFromRoutedEvent,
  extractRailEventsFromRouteData,
  formatDate,
  isLive,
  mapEndDate,
  splitRightRailContent
} from './functions/utility-functions'
import { LiveEvent, LiveHub } from '@flocasts/flosports30-types/dist/entity'
import { notNullOrUndefined } from '../shared/functions/type-guards'
import { LiveEventStatus } from '../events/events.enum'
import { WINDOW } from '../app.injections'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  FEAT_SHOW_LIVE_LIKE_ON_CHEER_ID,
  FEAT_SHOW_LIVE_LIKE_ON_CHEER_VARIATION
} from '../shared/experimentation/experiment.model'
import { EventHubDataService } from '../events/services/event-hub-data.service'
import { ContainerPartial } from '@flocasts/experience-service-types'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'
import { LiveAnalyticsService } from './live-analytics.service'

const LIVE_LIKE_IDS = [11708, 50286]

const CAMERA_CONFIG_STARTING_VALUE = 1

interface IRoutedLinker {
  routerLink: string
}

interface IRealtimeEventResponseWithMappedStreams {
  event: IRealtimeEventResponseWithEventId
  mappedStreams: ReadonlyArray<IExtendedRealtimeCameraInfo>
}

const maybeElement =
  <T extends HTMLElement>(doc: HTMLDocument) =>
  (query: string) =>
    maybe(doc.querySelector<T>(query) as T)

const maybeGetBadWrapperElement = (doc: HTMLDocument) => maybeElement<HTMLDivElement>(doc)('div .flex-1')

const maybeGetAdSkinElement = (doc: HTMLDocument) => maybeElement<HTMLDivElement>(doc)('#ad-skin')

const setElementDisplay = (rd: Renderer2, val: string) => (elm: HTMLElement) => rd.setStyle(elm, 'display', val)

const setElementFlex = (rd: Renderer2, val: string) => (elm: HTMLElement) => rd.setStyle(elm, 'flex', val)

const fixParentContainer = (rd: Renderer2, doc: HTMLDocument, shouldShowHeader = false) => {
  if (!shouldShowHeader) {
    maybeGetBadWrapperElement(doc).tapSome(badWrapper => {
      setElementDisplay(rd, 'flex')(badWrapper)
      setElementFlex(rd, '1')
    })
  }

  maybeGetAdSkinElement(doc).tapSome(() => setElementDisplay(rd, 'none'))
}

const revertParentContainer = (rd: Renderer2, doc: HTMLDocument) => {
  maybeGetBadWrapperElement(doc).tapSome(badWrapper => {
    setElementDisplay(rd, 'initial')(badWrapper)
    setElementFlex(rd, 'none')
  })

  maybeGetAdSkinElement(doc).tapSome(() => setElementDisplay(rd, 'block'))
}

const fixFooter = (rd: Renderer2, doc: HTMLDocument) => {
  if (doc.querySelector('flo-footer')) {
    setElementDisplay(rd, 'none')
  }
}

const requireItemCountToShowList = (list: ReadonlyArray<any>) => (min: number) => list.length > min

const isConcluded = (str?: string) => str === 'CONCLUDED'

const isRealtimeEventLive = (evt: IRealtimeEventResponseWithEventId) => isLive(evt.eventStatus)

const mapEventStatusToOverlay = (status: string, isDVR: boolean | null): VideoPlayerScreenErrorCode => {
  switch (status) {
    case 'PRE-AIR':
      return VideoPlayerScreenErrorCode.PRE_AIR
    case 'INTERMISSION':
      return isDVR ? ({} as VideoPlayerScreenErrorCode) : VideoPlayerScreenErrorCode.INTERMISSION
    case 'INTERDAY':
      return isDVR ? ({} as VideoPlayerScreenErrorCode) : VideoPlayerScreenErrorCode.INTERMISSION
    case 'CONCLUDED':
      return isDVR ? ({} as VideoPlayerScreenErrorCode) : VideoPlayerScreenErrorCode.CONCLUDED
    default:
      return {} as VideoPlayerScreenErrorCode
  }
}

const mapToVideoPlayerErrorOverlay =
  (isActive: boolean) =>
  (eventStatus: string) =>
  (message?: string | null) =>
  (meta?: object) =>
  (isDVR: boolean | null) => {
    return isActive
      ? (typeof mapEventStatusToOverlay(eventStatus, isDVR) !== 'object' && {
          code: mapEventStatusToOverlay(eventStatus, isDVR),
          message,
          meta
        }) ||
          undefined
      : {
          code: VideoPlayerScreenErrorCode.STREAM_COMPLETED,
          message,
          meta
        }
  }

const streamDictToRealtimeCameraInfo =
  (streamDict: IRealtimeCameraInfoDictionary, event: IRealtimeEventResponseWithEventId) => (streamId: string) => {
    return {
      ...streamDict[streamId],
      streamId,
      eventId: event.liveEventId,
      id: streamId,
      isFreeEvent: event.premium !== 'PAID', // TODO possibility
      eventStatus: event.eventStatus || event.status,
      eventStartAt: event?.startAtNext || event.startAt,
      eventStatusMessage: event.eventStatusMessage,
      shortName: event.shortName,
      videoPlayerErrorOverlay: mapToVideoPlayerErrorOverlay(streamDict[streamId].isActive)(event.eventStatus)(
        event.eventStatusMessage
      )({
        eventStartAt: !!event.startAtNext ? event.startAtNext : event.startAt
      })(event.isDVR)
    } as IExtendedRealtimeCameraInfo
  }

const mapstr = (streamDict: IRealtimeCameraInfoDictionary, event: IRealtimeEventResponseWithEventId) =>
  Object.keys(streamDict).map(streamDictToRealtimeCameraInfo(streamDict, event))

const evtResponseToResponseWithMappedStreams = (
  evtResponse: IRealtimeEventResponseWithEventId
): IRealtimeEventResponseWithMappedStreams => {
  return {
    event: evtResponse,
    mappedStreams: mapstr(evtResponse.streams, evtResponse)
  }
}

const evtResponseSafelyToResponseWithMappedStreams = (evtResponse: IRealtimeEventResponseWithEventId) => {
  return (!!evtResponse && evtResponseToResponseWithMappedStreams(evtResponse)) || undefined
}

const currentUrlIdParamEqualsLiveEventId = (ar: ActivatedRoute) => (resp: IRealtimeEventResponseWithMappedStreams) =>
  resp.event.liveEventId === getIdFromSlug(ar.snapshot.params.id)

const applyAppStylesToDisableScroll = (appElement: HTMLElement) => (renderer: Renderer2) => {
  if (appElement) {
    renderer.setStyle(appElement, 'overflow', 'hidden')
    renderer.setStyle(appElement, 'max-height', 'none !important')
    renderer.setStyle(appElement, 'height', 'auto !important')
  }
}

const applyAppStylesToEnableScroll = (appElement: HTMLElement) => (renderer: Renderer2) => {
  if (appElement) {
    renderer.removeStyle(appElement, 'overflow')
    renderer.removeStyle(appElement, 'max-height')
    renderer.removeStyle(appElement, 'height')
  }
}

const isLiveOrConcludedRealTimeEvent = (realTimeEvent: IRealtimeEventResponseWithMappedStreams) => {
  return isLive(realTimeEvent.event.status) || isConcluded(realTimeEvent.event.status)
}

export const shouldShowRaceMonitor = (raceMonitorIds: string[], isLiveOrConcluded: boolean) => {
  if (!!raceMonitorIds && raceMonitorIds.length > 0) {
    return isLiveOrConcluded
  }
  return false
}

@Component({
  selector: 'flo-live',
  templateUrl: './live.component.html',
  styleUrls: ['./live.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiveComponent implements OnInit, OnDestroy, AfterViewInit {
  // Note: This ViewChild DOM query is why the player and tiles flicker/repaint
  // Should be rewritten to not push events down into this child cmp, but just set properties and read output from the child
  // Instead of gridRef.setSelectedId(id), just do [selectedId]="id" in the template
  // Instead of gridRef.setCount(4), just do count=4 in the template
  // Instead of gridRef.isIdSelected(), just look at the output subject selectedIdChangeInGridSource last value
  @ViewChild('gridRef')
  set gridRef(gridRef: FloGridListViewComponent<any>) {
    if (gridRef) {
      this.gridRefSource.next(gridRef)
    }
  }
  @ViewChildren(VideoPlayerComponent)
  set contentVideoElements(contentVideoElements: QueryList<VideoPlayerComponent>) {
    if (contentVideoElements) {
      this.contentVideoElementsSource.next(contentVideoElements)
    }
  }

  constructor(
    @Inject(DOCUMENT) private doc: HTMLDocument,
    @Inject(SPORT_DATA_SERVICE) private readonly sds: SportDataService,
    private ps: PlatformService,
    private rd: Renderer2,
    private ar: ActivatedRoute,
    private les: LiveEventService,
    private evs: EventsService,
    private vs: VerticalService,
    private log: LoggingService,
    private rt: Router,
    private auth: AuthService,
    private segment: SegmentService,
    private appRef: ApplicationRef,
    private ds: DeviceService,
    private injectionService: InjectionService,
    private dp: DatePipe,
    private ns: NodeService,
    private uhs: UpdateHeadService,
    private userAgentService: UserAgentService,
    private es: ExperimentationService,
    private eventHubDataService: EventHubDataService,
    private readonly liveAnalyticsService: LiveAnalyticsService,
    @Inject(WINDOW) private readonly window: Window
  ) {
    combineLatest([this.selectedElementChangeSource, this.gridRef$]).subscribe(ref => {
      this.fsRef = isIphone() ? ref[0] : ref[1].elmRef.nativeElement
    })
  }

  public fsRef: HTMLElement

  @ViewChild('videosContainer') videosContainerRef?: ElementRef<HTMLDivElement>
  @ViewChildren('videoWrapper') videoWrapper?: QueryList<HTMLDivElement>

  public showMobileShadow = false

  // varsity logo or flo logo
  readonly liveLogo$ = this.vs.siteId$.pipe(
    map(siteId => (siteId === SiteIds.VARSITY ? 'varsity_white_logo' : 'flo_tomahawk')),
    shareReplay(1)
  )

  // if milesplit hide the upcoming and links in the right rail
  readonly isMileSplit$ = this.vs.isMileSplit$.pipe(shareReplay(1))

  /**
   * Update canonical url based on live event in route data.
   */
  private updateCanonicalUrl: Subscription = this.ar.data
    .pipe(
      filter(data => !data.error && data.liveEvent),
      map(data => data.liveEvent),
      take(1)
    )
    .subscribe((liveEvent: LiveEvent) => {
      const canonical = buildCanonicalUrl(addSlugToUrl(liveEvent.live_event_url, liveEvent.slug))
      this.uhs.addElementToHead(canonical)
    })

  public fetchRealtimeRouteData$ = this.ar.data.pipe(
    map<any, IRealtimeEventResponseWithEventId>(a => a.realtimeEventInfo),
    shareReplay(1)
  )

  public readonly liveEvent$ = combineLatest([this.fetchRealtimeRouteData$, this.vs.siteId$]).pipe(
    flatMap(([fromRouteData, siteId]) => {
      return this.les.getLiveEventConsolidatedWithLiveEventsOnly$(fromRouteData.liveEventId, siteId).pipe(
        catchError((cmsLoadError: HttpErrorResponse) => {
          this.log.warn(cmsLoadError.message)
          return of({} as LiveHub)
        }),
        map(cms => {
          return {
            fromRouteData,
            cms
          }
        })
      )
    }),
    flatMap(res => {
      return combineLatest([
        this.les.getLiveEventRealtimeStream(res.fromRouteData.liveEventId).pipe(
          map(b => b.valueOr({} as IRealtimeEventResponseWithEventId)),
          startWith(res.fromRouteData),
          catchError(() => of(res.fromRouteData)),
          map(evtResponseSafelyToResponseWithMappedStreams),
          takeWhile(currentUrlIdParamEqualsLiveEventId(this.ar)),
          takeUntil(this.ngOnDestroy$)
        )
      ]).pipe(
        map(realtime => {
          return {
            realtime: realtime[0],
            cms: res.cms
          }
        })
      )
    }),
    shareReplay(1)
  )

  public readonly enableTattoo$ = this.liveEvent$.pipe(
    map(liveEventObject => liveEventObject.realtime),
    filter<IRealtimeEventResponseWithMappedStreams>(Boolean),
    map(realtime => realtime.event),
    filter<IRealtimeEventResponseWithEventId>(Boolean),
    map(event => event.enableTattoo),
    shareReplay(1)
  )

  private readonly contentVideoElementsSource = new ReplaySubject<QueryList<VideoPlayerComponent>>(1)
  private readonly gridRefSource = new ReplaySubject<FloGridListViewComponent<any>>(1)
  private readonly cameraConfigSource = new Subject<number>()
  private readonly playerControlsSource = new Subject<VideoPlayerControlsPlayToggleClickEvent>()
  public readonly volumeStateSource = new Subject<Partial<IVolumeControls>>()
  public readonly adVolumeStateSource = new Subject<Partial<IVolumeControls>>()
  public readonly adPlayingSource = new ReplaySubject<boolean>(1)
  public readonly prerollAdPlayingSource = new ReplaySubject<boolean>(1)
  private readonly qualityControlStateSource = new Subject<IVideoQuality>()
  private readonly qualityValueSource = new Subject<number>()
  private readonly videoBufferAmountSource = new Subject<number>()
  private readonly videoCurrentTimeSource = new Subject<any>()
  private readonly playToggleSource = new Subject<any>()
  private readonly pauseVideoOnSeekingSource = new Subject<boolean>()
  private readonly videoPositionChangeSource = new Subject<number>()
  private readonly videoSkipForwardSource = new Subject<IVideoSkip>()
  private readonly videoSkipBackwardSource = new Subject<IVideoSkip>()
  private readonly ngOnDestroy$ = new Subject<void>()
  public readonly goToLivePlayHeadSource = new Subject<boolean>()
  public readonly selectedIdChangeInGridSource = new ReplaySubject<any>(1)
  public readonly playerControl$ = this.playerControlsSource.pipe(shareReplay(1))
  public readonly adPlaying$ = this.adPlayingSource.pipe(startWith(false), shareReplay(1))
  public readonly prerollAdPlaying$ = this.prerollAdPlayingSource.pipe(startWith(false), shareReplay(1))
  public readonly volumeState$ = this.volumeStateSource.pipe(
    distinctUntilChanged((x, y) => {
      return (
        x.isSelectedStream === y.isSelectedStream &&
        x.isMuted === y.isMuted &&
        x.selectedStreamId === y.selectedStreamId &&
        x.volume === y.volume
      )
    }),
    scan((oldVolume, newVolume) => ({ ...oldVolume, ...newVolume })),
    shareReplay(1)
  )

  public readonly adVolumeState$ = this.adVolumeStateSource.pipe(
    distinctUntilChanged((x, y) => {
      return (
        x.isSelectedStream === y.isSelectedStream &&
        x.isMuted === y.isMuted &&
        x.selectedStreamId === y.selectedStreamId &&
        x.volume === y.volume
      )
    }),
    scan((oldVolume, newVolume) => ({ ...oldVolume, ...newVolume })),
    shareReplay(1)
  )

  public readonly currentVolumeState$ = combineLatest([this.adPlaying$, this.adVolumeState$, this.volumeState$]).pipe(
    map(([isAdPlaying, adVol, liveVol]) => {
      const selectedVolume = isAdPlaying ? adVol : liveVol
      return selectedVolume
    }),
    shareReplay(1)
  )

  public readonly qualityControlState$ = this.qualityControlStateSource.pipe(distinctUntilChanged())
  public readonly qualityLevel$ = this.qualityValueSource
  public readonly videoCurrentTime$ = this.videoCurrentTimeSource.pipe(shareReplay(1))
  public readonly pauseVideoOnSeeking$ = this.pauseVideoOnSeekingSource
  public readonly videoPositionChange$ = this.videoPositionChangeSource
  public readonly videoBufferAmount$ = this.videoBufferAmountSource.pipe(distinctUntilChanged())
  public readonly videoSkipForward$ = this.videoSkipForwardSource.pipe(shareReplay(1))
  public readonly videoSkipBackward$ = this.videoSkipBackwardSource.pipe(shareReplay(1))
  public readonly playToggle$ = this.playToggleSource
  public readonly gridRef$ = this.gridRefSource
  public readonly panelToggledFormControl = new UntypedFormControl(false)
  public readonly panelToggled$ = this.panelToggledFormControl.valueChanges
  public readonly goToLivePlayHead$ = this.goToLivePlayHeadSource.pipe(
    map(res => ({ clicked: res })),
    shareReplay(1)
  )
  public readonly isAtLiveHead$ = this.videoCurrentTime$.pipe(map(res => res.isAtLiveHead))
  public readonly contentVideoElements$ = this.contentVideoElementsSource
  public readonly selectedIdChangeInGrid$ = this.selectedIdChangeInGridSource
  public readonly selectedElementChangeSource = new Subject<HTMLElement>()
  public readonly cameraConfig$ = this.cameraConfigSource.pipe(
    startWith(CAMERA_CONFIG_STARTING_VALUE),
    distinctUntilChanged(),
    shareReplay(1)
  )

  private readonly ngAfterViewInit$ = new ReplaySubject<void>(1)

  public onUnmuteClick(volumeState: { isMuted: boolean }): void {
    const newMuteState = { isMuted: !volumeState.isMuted }
    this.adPlaying$.pipe(take(1)).subscribe(adPlaying => {
      if (adPlaying) {
        this.adVolumeStateSource.next(newMuteState)
      } else {
        this.volumeStateSource.next(newMuteState)
      }
    })
  }

  public showMuteButton$ = combineLatest([
    this.volumeState$.pipe(map(volumeControls => !!volumeControls.isMuted)),
    this.adPlaying$
  ]).pipe(
    map(([isMuted, isAdPlaying]) => isMuted || isAdPlaying),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  readonly nonVideoSettings$ = combineLatest([this.vs.siteSettings$, this.auth.isPremium$, this.auth.isAdmin$]).pipe(
    map(([verticalSettings, isPremiumUser, isAdmin]) => {
      return {
        verticalSettings,
        isPremiumUser,
        isAdmin
      }
    }),
    shareReplay(1)
  )

  public readonly videoAnalyticsData$ = combineLatest([
    this.nonVideoSettings$,
    this.auth.userIdentity$,
    this.auth.isPremium$,
    this.liveEvent$
  ]).pipe(
    map(([nonVideoSettings, userIdentity, isPremiumUser, liveEvent]) => ({
      vertical: nonVideoSettings.verticalSettings.site_name,
      username: (!!userIdentity && (userIdentity.id || userIdentity.email || userIdentity.username)) || '0',
      liveEvent: {
        ...liveEvent,
        cms: {
          ...liveEvent.cms,
          event: liveEvent?.cms?.event || {}
        }
      },
      isPremiumUser
    })),
    map(data => {
      const shortName =
        data &&
        data.liveEvent &&
        data.liveEvent.realtime &&
        data.liveEvent.realtime.event &&
        data.liveEvent.realtime.event.shortName
      const shortTitle =
        data && data.liveEvent && data.liveEvent.cms && data.liveEvent.cms.event && data.liveEvent.cms.event.short_title
      const eventName = shortName || shortTitle
      return {
        'extraparam.1': data.vertical,
        'extraparam.2': data.liveEvent.cms.id,
        'content.title': `${data.isPremiumUser ? '' : '*'}${eventName}`,
        'extraparam.6': data.isPremiumUser ? 'Full View' : 'Preview',
        'extraparam.8': 'flosports-webapp - live beta',
        username: data.username,
        'content.isLive': true
      }
    }),
    distinctUntilChanged<Record<string, string | boolean | number | undefined>>((x, y) => {
      return A.foldMap(monoidAll)((key: string) => x[key] === y[key])(Object.keys(x))
    }),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  // Change this to routedLiveEvent and routedCmsInfo?

  public realTimeEvent$ = this.liveEvent$.pipe(
    map(event => event.realtime),
    filter<IRealtimeEventResponseWithMappedStreams>(Boolean),
    shareReplay(1)
  )

  public realTimeCmsInfo$ = this.liveEvent$.pipe(
    map(event => event.cms),
    filter<LiveHub>(Boolean),
    shareReplay(1)
  )

  public realTimeLiveEvent$ = this.realTimeEvent$.pipe(
    map(realtime => realtime.event),
    filter<IRealtimeEventResponseWithEventId>(Boolean),
    shareReplay(1)
  )

  public realTimeEventStreams$ = this.realTimeEvent$.pipe(
    map(realtime => realtime.mappedStreams),
    shareReplay(1)
  )

  public activeStreams$ = combineLatest([
    this.realTimeEventStreams$.pipe(filterSortStreamObs),
    // Note: Without this startWith on gridRef$, live player will not render
    this.gridRef$.pipe(startWith({} as FloGridListViewComponent<any>)),
    this.videoAnalyticsData$,
    this.selectedIdChangeInGrid$.pipe(startWith(0))
  ]).pipe(
    map(([eventStreams, gridRef, analytics]) => {
      return eventStreams.map(stream => ({
        ...stream,
        selectedStream: gridRef.isIdSelected && gridRef.isIdSelected(stream.streamId),
        isActive: gridRef.isItemInGrid && gridRef.isItemInGrid(stream),
        videoAnalytics: {
          ...analytics,
          'extraparam.3': stream.shortName,
          'extraparam.4': stream.id
        }
      }))
    }),
    shareReplay(1)
  )

  public eventSchedule$ = this.realTimeCmsInfo$.pipe(
    filter(cms => !!cms.event),
    map(cms => cms.event?.id),
    distinctUntilChanged(),
    flatMap(eventId => this.les.getEventSchedule(eventId)),
    takeUntil(this.ngOnDestroy$)
  )

  public activeStreamsLength$ = this.activeStreams$.pipe(map(streams => streams.length))

  private showStreamsTab$ = this.activeStreams$.pipe(
    map(streams => requireItemCountToShowList(streams)(1)),
    shareReplay(1)
  )

  public streamQueryParam$ = this.ar.queryParams.pipe(
    map<Params, string>(qp => qp.streamId),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public initialStreamInList$ = combineLatest([this.activeStreams$, this.streamQueryParam$]).pipe(
    map(([streams, qp]) => (qp && streams.find(stream => stream.id === qp)) || streams[0]),
    filter<any>(Boolean),
    distinctUntilChanged((prev, curr) => prev.streamId === curr.streamId),
    shareReplay(1)
  )

  public liveEventId$ = this.realTimeLiveEvent$.pipe(
    map(event => event.liveEventId),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public nextStreamId$ = combineLatest([this.liveEventId$, this.initialStreamInList$]).pipe(
    map(a => a[1].streamId),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public readonly eventHasVersusSportData$ = combineLatest([
    this.realTimeEvent$,
    this.vs.isVersusVerticalWithSportData$
  ]).pipe(
    flatMap(([liveEvent, isVersusVertical]) =>
      iif(
        () => isVersusVertical && (liveEvent.event ? isRealtimeEventLive(liveEvent.event) : false),
        this.sds.liveEventHasSportData(liveEvent.event.liveEventId).pipe(distinctUntilChanged(), shareReplay(1)),
        of(false)
      )
    ),
    distinctUntilChanged(),
    shareReplay(1)
  )

  private readonly shouldQueryFirestoreForSportData = (
    eventHasVersusSportData: boolean,
    stream: IExtendedRealtimeCameraInfo
  ) => {
    return (
      eventHasVersusSportData && !isNaN(Number(stream.streamId)) && !this.streamObservablesDict[Number(stream.streamId)]
    )
  }

  /**
   * Purpose of this dict is to limit firestore usage
   */
  streamObservablesDict: {
    [streamId: number]: Observable<SportDataSummary | undefined>
  } = {}
  private readonly mapStreamsToTiles = (
    streams: ReadonlyArray<IExtendedRealtimeCameraInfo>,
    cameraCount: number,
    isDVR: boolean,
    eventHasVersusSportData: boolean,
    adMetadata: LiveEventAdMetadata
  ): ReadonlyArray<StreamTile & IExtendedRealtimeCameraInfo> => {
    return streams.map(stream => {
      if (this.shouldQueryFirestoreForSportData(eventHasVersusSportData, stream)) {
        this.streamObservablesDict[Number(stream.streamId)] = this.sds
          .getMostRecentSportDataSummaryById(stream.eventId, Number(stream.streamId))
          .pipe(distinctUntilChanged(), shareReplay(1))
      }
      const inMultiCam = cameraCount > 1
      const canRemove = inMultiCam && stream.isActive
      return {
        ...stream,
        canAdd: inMultiCam && !canRemove,
        canRemove,
        canPlay: stream.eventStatus !== RealtimeEventStatus.CONCLUDED || isDVR,
        canShowSnapshot: stream.eventStatus === RealtimeEventStatus.LIVE,
        isPlaying: stream.selectedStream,
        videoPlayerMeta: {
          eventId: stream.eventId,
          streamId: stream.id,
          eventStartAt: stream.eventStartAt,
          eventStatus: stream.eventStatus as LiveEventStatus,
          eventStatusMessage: stream.eventStatusMessage,
          eventName: stream.name,
          eventShortName: stream.shortName,
          ...adMetadata
        },
        videoPlayerErrorOverlay:
          stream.videoPlayerErrorOverlay === undefined
            ? ({} as VideoPlayerScreenErrorCode)
            : stream.videoPlayerErrorOverlay,
        sportData$: this.streamObservablesDict[Number(stream.streamId)] || undefined,
        eventHasVersusSportData
      }
    })
  }

  public adMetadata$ = this.liveEventId$.pipe(
    flatMap(id => this.les.getLiveEventAdMetadata(id)),
    map(res => res.adMetadata),
    filter(notNullOrUndefined)
  )

  public readonly streamTiles$ = combineLatest([
    this.activeStreams$,
    this.cameraConfig$,
    this.eventHasVersusSportData$,
    this.adMetadata$.pipe(startWith({} as LiveEventAdMetadata))
  ]).pipe(
    map(([streams, cameraCount, eventHasVersusSportData, adMetadata]) =>
      this.mapStreamsToTiles(streams, cameraCount, true, eventHasVersusSportData, adMetadata)
    ),
    shareReplay(1)
  )

  public eventStatusError$ = this.auth.isPremium$.pipe(
    filter(Boolean),
    switchMap(() => this.streamTiles$),
    map(streamTiles => streamTiles[0]),
    filter(streamTile => !!streamTile),
    distinctUntilKeyChanged<StreamTile>('eventStatus'),
    map(stream => stream.videoPlayerErrorOverlay)
  )

  public streamName$ = combineLatest([this.activeStreams$, this.selectedIdChangeInGrid$]).pipe(
    map(([streams, selectedId]) => streams.filter(stream => stream.id === selectedId)),
    map(([stream]) => (stream ? stream.name : 'Select Stream')),
    shareReplay(1)
  )

  public cameraChangeEventSub = this.cameraConfig$
    .pipe(withLatestFrom(this.gridRef$), takeUntil(this.ngOnDestroy$))
    .subscribe(([cameraCount, gridRef]) => gridRef.setCount(cameraCount))

  public realTimeLiveEventStatus$ = this.realTimeLiveEvent$.pipe(
    map(event => event.eventStatus),
    filter(Boolean),
    shareReplay(1)
  )

  public tabSelected$ = this.ar.queryParams.pipe(map(qp => qp.tab || 'firstTab'))

  public initialFill$ = this.nextStreamId$.pipe(map(streamId => ({ 0: streamId })))

  public selectedStreamChangeSubscription = this.nextStreamId$
    .pipe(withLatestFrom(this.gridRef$), takeUntil(this.ngOnDestroy$))
    .subscribe(res => {
      this.cameraConfigSource.next(1)
      res[1].setSelectedId(res[0])
    })

  public isDVR$ = this.realTimeLiveEvent$.pipe(
    map(event => !!event.isDVR),
    shareReplay(1)
  )

  public rightRailProps$ = this.liveEvent$.pipe(
    withLatestFrom(this.isMileSplit$),
    map(([routedEvent, isMileSplit]) => {
      const dateFormatter = formatDate(this.dp)
      const railExtractor = extractRailEventsFromRouteData(this.dp)(routedEvent?.realtime?.event?.liveEventId)
      const cmsEvent = routedEvent?.cms?.event
      const cmsHub = routedEvent?.cms
      const realTimeEvent = routedEvent?.realtime?.event
      const today = railExtractor('Today')()(cmsHub?.upcoming_events || [])
      const upcoming = railExtractor('Upcoming')(true)(cmsHub?.upcoming_events || [])

      return {
        name: realTimeEvent?.name,
        liveId: realTimeEvent?.liveEventId,
        isLive: isLive(realTimeEvent?.status),
        eventHubLink: extractEventHubLinkFromRoutedEvent(routedEvent?.cms),
        dateRange: !!cmsEvent
          ? dateFormatter()(cmsEvent.start_date)(mapEndDate(cmsEvent.start_date)(cmsEvent.number_of_days))
          : '',
        upcoming,
        today,
        isTbd: !!routedEvent?.cms?.event?.start_time_tbd || false,
        onlyShowAds: isMileSplit || (today.shouldShow === 0 && upcoming.shouldShow === 0)
      }
    }),
    map(res => ({
      ...res,
      moreToday: splitRightRailContent(res.today),
      moreUpcoming: splitRightRailContent(res.upcoming)
    })),
    shareReplay(1)
  )

  public isLive$ = this.rightRailProps$.pipe(map(railProps => railProps.isLive))
  public isTbd$ = this.rightRailProps$.pipe(map(railProps => railProps.isTbd))

  public isDVRAndHlsSupported$ = this.isDVR$.pipe(
    filter(() => this.ps.isBrowser),
    map(isDVR => isDVR && Hls.isSupported())
  )

  // on initial load status to show certain messaging depending on the status of the event.
  public eventStatusOnLoad$ = this.realTimeLiveEvent$.pipe(
    distinctUntilChanged((oldValue, newValue) => oldValue.liveEventId === newValue.liveEventId),
    map(event => event.status),
    filter(Boolean),
    shareReplay(1)
  )

  public dvrStatus$ = combineLatest([this.eventStatusOnLoad$, this.isDVR$]).pipe(
    map(([eventStatus, isDVR]) => ({ eventStatus, isDVR })),
    shareReplay(1)
  )

  public isDVRConcluded$ = combineLatest([this.realTimeLiveEventStatus$, this.isDVR$, this.realTimeLiveEvent$]).pipe(
    map(([eventStatus, isDVR, realTimeEvent]) => realTimeEvent.eventStatus === 'CONCLUDED' && isDVR && realTimeEvent),
    map(realTimeEvent => realTimeEvent && { startDate: realTimeEvent.startAt, endDate: realTimeEvent.endAt }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public isDVRIntermission$ = combineLatest([this.realTimeLiveEventStatus$, this.isDVR$, this.realTimeLiveEvent$]).pipe(
    map(
      ([eventStatus, isDVR, realTimeEvent]) =>
        (eventStatus === 'INTERMISSION' || eventStatus === 'INTERDAY') && isDVR && realTimeEvent.startAtNext
    ),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public isDVRPreair$ = this.realTimeLiveEventStatus$.pipe(
    map(eventStatus => eventStatus === 'PRE-AIR'),
    distinctUntilChanged()
  )

  public dvrPreAirToLive$ = this.realTimeLiveEventStatus$.pipe(
    scan((acc, curr) => (acc === 'PRE-AIR' && curr === 'LIVE' ? true : curr)),
    map(res => res === true),
    distinctUntilChanged()
  )

  public showPlayerControls$ = combineLatest([
    this.realTimeEvent$,
    this.adPlaying$,
    this.isDVR$,
    this.isDVRPreair$
  ]).pipe(
    map(([liveEvent, adPlaying, isDVR, dvrPreAir]) => {
      return {
        isEventLive: liveEvent?.event?.eventStatus === 'LIVE' || false,
        adPlaying,
        isDVR,
        dvrPreAir
      }
    }),
    map(({ isEventLive, adPlaying, isDVR, dvrPreAir }) => {
      return canShowLiveVideoControls(isEventLive, adPlaying, dvrPreAir, isDVR)
    }),
    shareReplay(1)
  )

  public hideMultiCamControls$ = combineLatest([this.showPlayerControls$, this.showStreamsTab$]).pipe(
    map(([showPlayerControls, showStreamTab]) => !(showPlayerControls && showStreamTab))
  )

  public setSeoSub = this.realTimeCmsInfo$
    .pipe(
      map(cms => ({
        title: cms?.title,
        seo_description: cms?.event?.seo_description,
        asset: {
          url: cms?.event?.asset_url || ''
        }
      })),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(seo => this.ns.applyNodeMetaToPage(seo as INode))

  public eventAnalytics$ = this.realTimeCmsInfo$.pipe(
    map(cms => {
      return {
        node_id: cms?.event?.id,
        live_id: cms?.id
      }
    }),
    shareReplay(1),
    distinctUntilChanged((prev, curr) => prev.node_id === curr.node_id && prev.live_id === curr.live_id)
  )

  public readonly eventAddToCalendarProps$ = combineLatest([
    this.vs.siteName$,
    this.vs.apiUrl$,
    this.eventAnalytics$
  ]).pipe(
    map(([siteName, apiUrl, analytics]) => ({ siteName, apiUrl, event: { id: analytics.node_id } })),
    shareReplay(1)
  )

  public replaysTab$ = combineLatest([
    this.liveEvent$.pipe(distinctUntilChanged((a, b) => a.realtime.event.liveEventId === b.realtime.event.liveEventId)),
    this.tabSelected$.pipe(
      filter(tabName => tabName === 'replays'),
      startWith('replays')
    )
  ]).pipe(
    filter(([evt]) => !!evt.cms?.event),
    flatMap(([evt]) => {
      return this.evs.getEventVideos(evt.cms.event.id, 'recent', 12).pipe(
        map(response => {
          return response.data.map(video => {
            return {
              ...video,
              routerLink: `/events/${evt.cms.event.id}-${video.slug}/videos`,
              queryParams: { playing: video.id },
              live_event_status: evt?.cms?.event?.live_status
            }
          })
        })
      )
    }),
    map(videos => {
      return {
        ngIf: videos.length >= 1,
        showMoreButton: videos.length >= 12,
        videos,
        target: '_blank',
        btnRouterLink: videos[0]?.routerLink,
        queryParamsHandling: '',
        queryParamsFn: (video: IVideo) => ({ playing: video.id }),
        analyticsPropertiesFn: (video: IVideo & { live_event_status: string }) => ({
          name: 'Watch Replay',
          section: 'Events',
          node_type: 'Video',
          node_id: video.id,
          title: video.title,
          is_premium: video.premium,
          array_length: videos.length,
          live_event_status: video.live_event_status,
          item_order: videos.findIndex(a => a.id === video.id)
        })
      }
    }),
    shareReplay(1)
  )

  public renderReplaysTab$ = this.replaysTab$.pipe(map(a => a.ngIf))

  public ngDragStyle = {
    'background-color': 'black',
    height: '100%',
    width: '100%',
    display: 'flex',
    'justify-content': 'center',
    'align-items': 'center'
  }

  public readonly showStreamTile$: Observable<boolean> = combineLatest([
    this.activeStreams$,
    this.eventHasVersusSportData$
  ]).pipe(
    map(([streams, eventHasVsSportData]) => {
      const min = eventHasVsSportData ? 0 : 1
      return requireItemCountToShowList(streams)(min)
    }),
    shareReplay(1)
  )

  @HostListener('window:scroll', ['$event.target.scrollingElement'])
  applyScrollStyles(scrollingElement?: HTMLElement) {
    if (scrollingElement?.scrollTop !== 0) this.showMobileShadow = true
    else {
      this.showMobileShadow = false
    }
  }

  readonly collapseOverlay = () => {
    this.panelToggledFormControl.setValue(!this.panelToggledFormControl.value)
  }

  public rightRailAnalytics(toggled: boolean, eventProps: any): void {
    this.segment.track('Button Clicked', {
      name: toggled ? 'Show Right Rail' : 'Hide Right Rail',
      node_id: eventProps.node_id,
      live_id: eventProps.live_id
    })
  }

  ngOnInit() {
    this.listenForPanelToggleChange()
    this.realTimeEvent$.pipe(take(1)).subscribe(realTimeEvent => {
      this.liveAnalyticsService.sendPageCall(realTimeEvent.event.liveEventId, realTimeEvent.event.premium === 'PAID')
    })
  }

  ngAfterViewInit() {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  ngOnDestroy() {
    this.ensureAppScrollIsEnabled()
    this.revertRootStyleFixes()
    this.uhs.removeElement(CANONICAL_ID)
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  readonly applyRootStyleFixes = (shouldShowHeader = false) => {
    fixFooter(this.rd, this.doc)
    fixParentContainer(this.rd, this.doc, shouldShowHeader)
    if (this.ps.isBrowser) {
      setTimeout(() => fixParentContainer(this.rd, this.doc), 0)
    }
  }

  readonly getAppRootElement = (): HTMLDivElement => {
    return (
      !!this.appRef.components[0] &&
      (this.appRef.components[0].injector.get(ElementRef).nativeElement as HTMLDivElement)
    )
  }

  readonly revertRootStyleFixes = () => revertParentContainer(this.rd, this.doc)
  readonly ensureAppScrollIsEnabled = () => applyAppStylesToEnableScroll(this.getAppRootElement())(this.rd)
  readonly ensureAppScrollIsDisaled = () => applyAppStylesToDisableScroll(this.getAppRootElement())(this.rd)
  readonly navigateToV2Player = (ws: Window) => (url: string) => (ws.window.location.href = url)
  readonly playToggle = (playToggle: boolean) => this.playToggleSource.next({ isPaused: playToggle })
  readonly setInitialVideoQuality = (qualityState: IVideoQuality) => this.qualityControlStateSource.next(qualityState)
  readonly setVideoPlayState = (playState: VideoPlayerControlsPlayToggleClickEvent) =>
    this.playerControlsSource.next(playState)
  readonly changeVideoQuality = (qualityLevel: number) => this.qualityValueSource.next(qualityLevel)
  readonly updateVideoBufferAmount = (bufferAmount: number) => this.videoBufferAmountSource.next(bufferAmount)
  readonly updateVideoTime = (currentTime: number) => this.videoCurrentTimeSource.next(currentTime)
  readonly pauseVideoOnSeeking = (paused: boolean) => this.pauseVideoOnSeekingSource.next(paused)
  readonly videoPositionChange = (videoPosition: number) => this.videoPositionChangeSource.next(videoPosition)
  readonly videoSkipForward = (skipTime: IVideoSkip) => this.videoSkipForwardSource.next(skipTime)
  readonly videoSkipBackward = (skipTime: IVideoSkip) => this.videoSkipBackwardSource.next(skipTime)

  readonly updateCameraConfig = (count: number) => this.cameraConfigSource.next(count)

  readonly listenForPanelToggleChange = () => {
    combineLatest([this.ds.windowResized$.pipe(startWith(this.window)), this.panelToggled$])
      .pipe(
        map(([windowResized, panelToggled]) => panelToggled && windowResized && windowResized.innerWidth <= 993),
        takeUntil(this.ngOnDestroy$)
      )
      .subscribe(shouldDisable => {
        shouldDisable ? this.ensureAppScrollIsDisaled() : this.ensureAppScrollIsEnabled()
      })
    this.ar.url.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.panelToggledFormControl.setValue(false)
    })
  }

  readonly setSeo = (node: INode) => this.ns.applyNodeMetaToPage(node)

  readonly trackByTodaysLiveEvents: TrackByFunction<IRoutedLinker> = (_: number, item: IRoutedLinker) => item.routerLink

  readonly trackByUpcomingLiveEvents: TrackByFunction<IRoutedLinker> = (_index: number, item: IRoutedLinker) =>
    item.routerLink

  public readonly tabSelected = (tab: ITab) =>
    this.rt.navigate([], {
      queryParams: { tab },
      queryParamsHandling: 'merge'
    })

  /**
   * @todo: replace this call by adding the necessary node information to the
   *        consolidated event endpoint.
   */
  private readonly eventHub$ = this.liveEvent$.pipe(
    map(res => res.cms?.event?.id || null),
    distinctUntilChanged(),
    switchMap(id => (id !== null ? this.evs.getEvent(id) : of(null))),
    catchError(err => {
      this.log.error('Unable to get Event Hub from API for Live Event.', { err })
      return of(null)
    }),
    map(r => (r !== null ? r.data : null)),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private readonly adTargeting$ = combineLatest([
    this.eventHub$,
    this.liveEventId$,
    this.auth.isPremium$.pipe(take(1))
  ]).pipe(
    map(([ev, liveId, isPremium]): AdTargeting => {
      return ev !== null
        ? {
            contentIds: ev.aggregated_node_ids,
            categorySlugs: ev.aggregated_category_slugs,
            liveId,
            isPremium,
            analyticsTracking: ev
          }
        : {
            liveId,
            isPremium
          }
    }),
    shareReplay(1)
  )

  // Ads
  public AdComponent = import('../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly initialAdhesionProps$: Observable<AdComponentInputs> = this.adTargeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('live/adhesion', AD_ADHESION_SIZE, 'ad-liveplayer-adhesion', targeting),
          additionalOpts: { maxRefreshTime: 30000 }
        }
      }
    }),
    take(1)
  )

  public readonly initialRightRailAdProps$: Observable<AdComponentInputs> = this.adTargeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('live/300x250_1', AD_RECTANGLE_LARGE_SIZE, 'ad-liveplayer-rail', targeting),
          additionalOpts: { maxRefreshTime: 45000 }
        }
      }
    }),
    take(1)
  )

  public readonly interstitialAdProps$: Observable<AdComponentInputs> = this.adTargeting$.pipe(
    map(targeting => {
      return {
        config: {
          adConfig: AdConfig('live/interstitial', AD_INTERSTITIAL_SIZE, 'ad-liveplayer-interstitial', targeting)
        }
      }
    }),
    take(1)
  )

  public readonly typeFormUrl$: Observable<string> = combineLatest([
    this.auth.userIdentity$,
    this.realTimeEvent$,
    this.realTimeCmsInfo$,
    this.vs.siteSettings$,
    this.streamName$,
    this.selectedIdChangeInGrid$
  ]).pipe(
    map(([userIdentity, liveEvent, eventCmsInfo, siteSettings, streamName, selectedStreamId]) => {
      const ua = this.userAgentService.userAgent
      const eventId = eventCmsInfo.event?.id

      return {
        app_name: 'web',
        device_model: ua.device,
        email: userIdentity?.email,
        event_title: liveEvent.event.name,
        node_id: eventId,
        os: ua.browser,
        os_version: ua.source,
        stream_title: streamName,
        stream_id: selectedStreamId,
        user_id: userIdentity?.id,
        vertical: siteSettings.site_name,
        event_id: eventId
      }
    }),
    map(buildTypeformStreamFeedbackUrlFromProps),
    distinctUntilChanged()
  )

  private injectTypeformScript = this.ngAfterViewInit$.pipe(take(1)).subscribe(() => {
    this.injectionService.injectScripts(this.rd, ['https://embed.typeform.com/embed.js'], true)
  })

  // CXP-2265: Code for LiveLike on FloCheer during Cheer Worlds
  private readonly isInLiveLikeVariation$ = this.es
    .isInVariation(FEAT_SHOW_LIVE_LIKE_ON_CHEER_ID, FEAT_SHOW_LIVE_LIKE_ON_CHEER_VARIATION, false)
    .pipe(shareReplay(1))

  public readonly showLiveLike$ = combineLatest([this.liveEventId$, this.isInLiveLikeVariation$]).pipe(
    map(([liveEventId, isInVariation]) => {
      if (LIVE_LIKE_IDS.includes(liveEventId)) {
        this.es.activate(FEAT_SHOW_LIVE_LIKE_ON_CHEER_ID)
      }

      return LIVE_LIKE_IDS.includes(liveEventId) && isInVariation
    }),
    shareReplay(1)
  )

  public readonly raceMonitorIds$: Observable<string[]> = this.eventHub$.pipe(
    /**
     * ContentNode type is out of date and does not have external_associations as a property
     * so needed the ts-ignore below
     * @see ContentNode
     *
     */
    // @ts-ignore
    map(event => event?.node?.external_associations),
    filter(notNullOrUndefined),
    map(eas => {
      // declaring variable here so it can be typed
      const externalAssociations: NodeExternalAssociation[] = eas
      const raceMonitorAssociations = externalAssociations.filter(ea => ea.external_source === 'racemonitor')
      return raceMonitorAssociations.map(rmEvent => rmEvent.external_id)
    }),
    shareReplay(1)
  )

  public readonly showRaceMonitor$ = combineLatest([this.raceMonitorIds$, this.realTimeEvent$]).pipe(
    map(([raceMonitorIds, realTimeEvent]) => {
      // Only show if the event is live or concluded AND it has race monitor id's.
      // We could also check site id for floracing here, but no non-racing events would ever have race monitor id's.
      return shouldShowRaceMonitor(raceMonitorIds, isLiveOrConcludedRealTimeEvent(realTimeEvent))
    }),
    shareReplay(1)
  )

  // Get game center HTML for given nodeId
  public readonly gameCenterInfo$ = combineLatest([this.realTimeCmsInfo$, this.vs.siteId$]).pipe(
    filter(([cmsInfo, siteId]) => !!cmsInfo?.event?.id && siteId === SiteIds.FLOHOCKEY),
    map(([cmsInfo, _]) => cmsInfo.event.id),
    distinctUntilChanged(),
    switchMap((nodeId: number) => this.eventHubDataService.getEventHubPartial(nodeId, 'info', null)),
    map(partial => {
      if (partial?.type === 'partial:container') {
        const container = partial as ContainerPartial
        if (container.data.title?.text === 'Game Center') {
          return container?.data?.items?.find(item => item.type === 'html-view')
        }
      }
      return null
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )
}
